import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { EmailTemplate } from 'src/app/models/email-template';
import { EmailsTemplatesService } from 'src/app/services/emails-templates.service';
import { FormMode } from '../../common/form-mode';
import { WorkflowDataInterface } from '../workflow-data.interface';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subject } from 'rxjs';
import { SendMailActionData } from 'src/app/components/designer/models/send-mail-action-data';
import { ContextualMenuEvent } from '../contextual-menu-event';
import { NeedEmailTemplates } from '../need-email-templates';

@Component({
    selector: 'app-workflow-action-send-mail-data',
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule, FaIconComponent, MatTooltipModule, MatCheckboxModule],
    templateUrl: './workflow-action-send-mail-data.component.html',
    styleUrl: './workflow-action-send-mail-data.component.scss'
})
export class WorkflowActionSendMailDataComponent implements WorkflowDataInterface, OnInit, NeedEmailTemplates {
    /**
     * Liste des icones utilisés dans le template.
     */
    public faCircleInfo = faCircleInfo;

    /**
     * Données affichées par le composant dynamique.
     */
    public _data: SendMailActionData;

    /**
     * Spécifie les données affichées par le composant dynamique.
     */
    set data(value: SendMailActionData | string) {
        if (typeof value === 'string') {
            this._data = JSON.parse(value);
        } else {
            this._data = value as SendMailActionData;
        }

        this.fillForm();
    }

    /**
     * Obtient les données affichées par le composant dynamique.
     */
    get data(): SendMailActionData {
        this.fillData();
        return this._data;
    }

    /**
     * Mode d'affichage du formulaire.
     */
    mode: FormMode;

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    public get dirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    /**
     * Source du menu contextuel.
     */
    contextualMenuSource: Subject<ContextualMenuEvent> = new Subject<ContextualMenuEvent>();

    /**
     * Liste des modèles de mails.
     */
    public _templates: EmailTemplate[];

    /**
     * Spécifie la liste des modèles de mails.
     */
    set templates(value: EmailTemplate[]) {
        this._templates = value;
        this.fillForm();
    }

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Constructeur.
     */
    constructor(private emailsTemplatesService: EmailsTemplatesService) {}

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.dataForm = new UntypedFormGroup({
            templateId: new UntypedFormControl({ value: '' }, [Validators.required]),
            autoSendEnabled: new UntypedFormControl(true),
            dontSendIfRecipientMissingEnabled: new UntypedFormControl(true),
        });

        this.fillForm();
    }

    /**
     * Indique si le formulaire est valide.
     */
    public isValid(): boolean {
        return true;
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Remplit le formulaire avec les données.
     */
    private fillForm() {
        if (this.dataForm) {
            if (this._data) {
                if (this._templates) {
                    this.dataForm.get('templateId').setValue(this._data.templateId);
                }

                this.dataForm.get('autoSendEnabled').setValue(this._data.autoSend);
                this.dataForm.get('dontSendIfRecipientMissingEnabled').setValue(this._data.dontSendIfRecipientMissing);
            }
        }
    }

    /**
     * Met à jour les données à partir du formulaire.
     */
    private fillData() {
        let updatedData: SendMailActionData = new SendMailActionData();
        updatedData.templateId = this.dataForm.get('templateId').value;
        updatedData.autoSend = this.dataForm.get('autoSendEnabled').value;
        updatedData.dontSendIfRecipientMissing = this.dataForm.get('dontSendIfRecipientMissingEnabled').value;

        this._data = updatedData;
    }
}
